import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
} from "@sportscanner/ui";
import { apiPatch, getApiErrorCode } from "@/lib/api";
import { type Permission } from "@/lib/me";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import type { AdminRow } from "./admins-list";

const PERMISSION_KEYS: readonly Permission[] = [
  "view_bookings",
  "create_booking",
  "cancel_booking",
  "edit_booking",
  "manage_blocks",
  "manage_closures",
  "view_revenue",
  "override_booking_limits",
] as const;

// Mirrors STAFF_PERMISSION_DEFAULTS in apps/api/src/lib/permissions.ts. Used as
// the starting point when a STAFF row has no permissions JSON yet (shouldn't
// happen post-migration, but be defensive).
const STAFF_PERMISSION_DEFAULTS: Record<Permission, boolean> = {
  view_bookings: true,
  create_booking: true,
  cancel_booking: true,
  edit_booking: false,
  manage_blocks: true,
  manage_closures: false,
  view_revenue: false,
  override_booking_limits: false,
};

interface Props {
  clubId: string;
  row: AdminRow;
  onClose: () => void;
  onSaved: () => void;
}

function resolveInitial(
  current: AdminRow["permissions"],
): Record<Permission, boolean> {
  const out = { ...STAFF_PERMISSION_DEFAULTS };
  if (!current) return out;
  for (const key of PERMISSION_KEYS) {
    if (typeof current[key] === "boolean") out[key] = current[key]!;
  }
  return out;
}

export default function EditPermissionsDialog({
  clubId,
  row,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<Record<Permission, boolean>>(
    () => resolveInitial(row.permissions),
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      apiPatch(
        `/api/admin/clubs/${clubId}/admins/${row.id}/permissions`,
        permissions,
      ),
    onSuccess: () => {
      toast.success(t("admins.toast.permissionsSaved"));
      onSaved();
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mutation.isPending) return;
    setErrorCode(null);
    mutation.mutate();
  }

  const displayName = `${row.firstName} ${row.lastName}`.trim() || row.email;

  return (
    <ResponsiveDialog
      open
      onOpenChange={(o) => {
        if (mutation.isPending) return;
        if (!o) onClose();
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("admins.editPermissions.title")}</DialogTitle>
          <DialogDescription>
            {t("admins.editPermissions.description", { name: displayName })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}

          <div className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
            {PERMISSION_KEYS.map((key) => (
              <div
                key={key}
                className="flex items-start justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <Label
                    htmlFor={`mgmt-perm-${key}`}
                    className="text-sm font-medium text-neutral-800 cursor-pointer"
                  >
                    {t(`invite.permissions.${key}.label`)}
                  </Label>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {t(`invite.permissions.${key}.description`)}
                  </p>
                </div>
                <Switch
                  id={`mgmt-perm-${key}`}
                  checked={permissions[key]}
                  onCheckedChange={(checked) =>
                    setPermissions((prev) => ({ ...prev, [key]: checked }))
                  }
                  disabled={mutation.isPending}
                />
              </div>
            ))}
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mutation.isPending}
              className="cursor-pointer"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="cursor-pointer bg-primary-600 hover:bg-primary-700 text-white"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("common.save")
              )}
            </Button>
          </DialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
